import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { logoutUser, setRawDataset } from "../slices";
import { tokenRefreshService } from "../../services/tokenRefreshService";
import { isJourneyActive } from "../../tutorials/journeyState";

// In-memory cache for pending dataset fetches to prevent duplicate requests
const pendingFetches = new Map();

/** Finance problems hidden from the app list (API still returns them). */
const HIDDEN_PROBLEM_IDS = new Set([
    "6849553197fddfbf81966dd8",
    "6849549197fddfbf81966db2",
    "6849540797fddfbf81966d8c",
    "684952fa97fddfbf81966d64",
    "6840292783c2a953ea489741",
    "683edca84590cdd6363cc3da",
    "683edae4ab30987fa7b74503",
    "683eda05ab30987fa7b744db",
    "68300468f805777f268b9e04",
    "67a0a8c2b8c0f6973cb64c24",
    "67a0a8b5b8c0f6973cb64bff"
]);

function filterHiddenProblems(response) {
    const list = response?.data;
    if (!Array.isArray(list)) return response;
    return {
        ...response,
        data: list.filter((p) => p?._id && !HIDDEN_PROBLEM_IDS.has(p._id)),
    };
}

const datalyzeEndpoint = import.meta.env.VITE_DATALYZE_ENDPOINT;

const baseQueryWithAuth = async (args, api, extraOptions) => {
    const baseQuery = fetchBaseQuery({
        baseUrl: datalyzeEndpoint,
        prepareHeaders: async (headers) => {
            const accessToken = await tokenRefreshService.getValidAccessToken();
            if (accessToken) {
                headers.set('Authorization', `Bearer ${accessToken}`);
            }
            return headers;
        },
    });

    const result = await baseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401) {
        api.dispatch(logoutUser());
    }

    return result;
};

export const datalyzeProblemApis = createApi({
    reducerPath: "datalyzeProblemApis",
    baseQuery: baseQueryWithAuth,
    refetchOnFocus: true,
    tagTypes: ['Problems', 'Leaderboard', 'Problem', 'Domains'],
    endpoints: (builder) => ({
        getDomains: builder.query({
            query: (payload) => {
                const params = new URLSearchParams();
                if (payload?.location) params.set("location", payload.location);
                return {
                    url: `domains?${params.toString()}`,
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                    },
                };
            },
            providesTags: ['Domains'],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                queryFulfilled.catch((error) => {
                    if (error?.error?.status === 401) {
                        dispatch(logoutUser());
                    }
                });
            },
        }),
        getAllProblems: builder.query({
            query: (payload) => {
                const params = new URLSearchParams();
                if (payload?.location) params.set("location", payload.location);
                if (payload?.category) params.set("category", payload.category);
                return {
                    url: `problems?${params.toString()}`,
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                    },
                };
            },
            transformResponse: filterHiddenProblems,
            providesTags: ['Problems'],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                queryFulfilled.catch((error) => {
                    if (error?.error?.status === 401) {
                        dispatch(logoutUser());
                    }
                });
            },
        }),
        getProblemById: builder.query({
            query: (payload) => ({
                url: `problems/${payload?.id}?location=${payload?.location}`, // Adjust endpoint to match the API
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            }),
            providesTags: (result, error, arg) => [{ type: 'Problem', id: arg?.id }],
            async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
                try {
                    const { data } = await queryFulfilled;

                    // Extract problem data (handle both response formats)
                    const problemData = data?.data || data;
                    const dataSource = problemData?.dataSource;
                    const problemId = arg?.id;

                    // Prefetch dataset if dataSource exists and not already cached
                    if (dataSource && problemId) {
                        // Check if already cached in Redux
                        const state = getState();
                        const rawDatasets = state?.rawDatasets?.datasets || {};
                        const isCached = rawDatasets[problemId]?.data?.length > 0;

                        if (!isCached) {
                            // Check if fetch already in progress
                            if (pendingFetches.has(dataSource)) {
                                // Wait for existing fetch
                                pendingFetches.get(dataSource)
                                    .then(dataset => {
                                        dispatch(setRawDataset({ problemId, dataset, dataSource }));
                                    })
                                    .catch(() => {
                                        // Silent fail - components will fetch on demand
                                    });
                            } else {
                                // Start new fetch in background (don't block)
                                const fetchPromise = fetch(dataSource)
                                    .then(res => res.json())
                                    .then(dataset => {
                                        pendingFetches.delete(dataSource);
                                        dispatch(setRawDataset({
                                            problemId,
                                            dataset,
                                            dataSource
                                        }));
                                        return dataset;
                                    })
                                    .catch(err => {
                                        pendingFetches.delete(dataSource);
                                        console.warn('Failed to prefetch dataset:', err);
                                        // Silent fail - components will fetch on demand
                                        throw err;
                                    });

                                pendingFetches.set(dataSource, fetchPromise);
                            }
                        }
                    }
                } catch (error) {
                    // Handle 401 errors (existing logic)
                    if (error?.error?.status === 401) {
                        dispatch(logoutUser());
                    }
                }
            },
        }),
        getLeaderboard: builder.query({
            query: () => ({
                url: `/leaderboard`, // Adjust endpoint to match the API
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            }),
            providesTags: ['Leaderboard'],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                queryFulfilled.catch((error) => {
                    if (error?.error?.status === 401) {
                        dispatch(logoutUser());
                    }
                });
            },
        }),
        sendEmail: builder.mutation({
            query: (payload) => ({
                url: `/contact-us`, // Adjust endpoint to match the API
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: payload
            }),
        }),
        updateCoins: builder.mutation({
            query: (payload) => {
                const body = { ...payload.body, isTutorialMode: isJourneyActive() };
                return {
                    url: "trackings",
                    method: "PATCH",
                    body,
                    headers: {
                        "Content-Type": "application/json",
                    },
                };
            },
            invalidatesTags: (result, error, arg) => [
                'Problems',
                'Leaderboard',
                { type: 'Problem', id: arg?.body?.problemId }
            ],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                queryFulfilled
                    .then((_result) => {
                        // Success handled silently
                    })
                    .catch((error) => {
                        if (error?.error?.status === 401) {
                            dispatch(logoutUser());
                        }
                    });
            },
        }),
        submitFeedback: builder.mutation({
            query: (payload) => ({
                url: `problems/${payload?.id}/feedback`,
                method: "POST",
                body: payload?.body,
                headers: {
                    "Content-Type": "application/json",
                },
            }),
            invalidatesTags: (result, error, arg) => [
                'Problems',  // Invalidate all problems list
                'Leaderboard',  // Invalidate leaderboard (in case feedback affects scoring)
                { type: 'Problem', id: arg?.id }  // Invalidate specific problem
            ],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                queryFulfilled.catch((error) => {
                    if (error?.error?.status === 401) {
                        dispatch(logoutUser());
                    }
                });
            },
        }),
        updateChartCompletion: builder.mutation({
            query: (payload) => ({
                url: `problems/${payload?.id}/chart-completion`,
                method: "PATCH",
                body: payload.body,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${payload.token}`, // Adding JWT token to headers
                },
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Problem', id: arg?.id }
            ],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                queryFulfilled.catch((error) => {
                    if (error?.error?.status === 401) {
                        dispatch(logoutUser());
                    }
                });
            },
        }),
    }),
});

export const {
    useGetDomainsQuery,
    useGetAllProblemsQuery,
    useGetProblemByIdQuery,
    useUpdateCoinsMutation,
    useGetLeaderboardQuery,
    useSendEmailMutation,
    useSubmitFeedbackMutation,
    useUpdateChartCompletionMutation
} = datalyzeProblemApis;